import { useEffect, useRef, useState, type FormEvent } from "react";
import { call } from "../huddle";
import { useLantern, useSession } from "../hooks";
import { locale, t, tn } from "../i18n";
import type { PastHuddle, UUID } from "../sync/types";
import { dayLabel, lasted } from "../timeline";
import { useCall } from "./Huddle";
import { IconHeadphones } from "./icons";
import { RecipientPicker, type To } from "./RecipientPicker";
import { Avatar, Badge, Card, EmptyState, Form, Page } from "./ui";

// HuddlesView is where one's calls are, as Slack's Huddles: the ones going
// on now in one's channels and conversations, starting one with a few
// people, those that ended, and each channel's, to start or join. Joining
// keeps one here; the call shows at the foot of the sidebar.
export function HuddlesView() {
  const session = useSession();
  const state = useLantern();
  const current = useCall();
  const [picking, setPicking] = useState(false);
  const [to, setTo] = useState<To[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState<PastHuddle[] | null>(null);
  const [more, setMore] = useState(false);
  const [recentError, setRecentError] = useState("");
  const [loadingOlder, setLoadingOlder] = useState(false);
  // deeper is whether older pages are shown, which a refresh keeps.
  const deeper = useRef(false);
  const message = (err: unknown) => (err instanceof Error ? err.message : String(err));

  // The recent calls, again whenever a call starts or ends: the newest page,
  // ahead of the older ones already shown.
  const going = [...state.huddles.keys()].sort().join();
  useEffect(() => {
    if (!state.huddlesOn) return;
    let live = true;
    session.api.recentHuddles().then(
      (r) => {
        if (!live) return;
        setRecentError("");
        const fresh = new Set(r.huddles.map((h) => h.id));
        setRecent((shown) => [...r.huddles, ...(deeper.current ? (shown ?? []).filter((h) => !fresh.has(h.id)) : [])]);
        if (!deeper.current) setMore(r.has_more);
      },
      (err) => live && setRecentError(message(err)),
    );
    return () => {
      live = false;
    };
  }, [session, state.huddlesOn, going]);
  const older = async () => {
    const last = recent?.at(-1);
    if (!last || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const r = await session.api.recentHuddles(last.ended_at, last.id);
      deeper.current = true;
      setRecent((shown) => {
        const have = new Set((shown ?? []).map((h) => h.id));
        return [...(shown ?? []), ...r.huddles.filter((h) => !have.has(h.id))];
      });
      setMore(r.has_more);
    } catch (err) {
      setRecentError(message(err));
    } finally {
      setLoadingOlder(false);
    }
  };

  async function join(channel: UUID) {
    setError("");
    setBusy(true);
    try {
      await call.join(session, channel);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  // start calls the people picked, in their conversation, or joins the
  // channel picked.
  async function start(e: FormEvent) {
    e.preventDefault();
    if (!to.length) return;
    const channel = to.find((x) => x.kind === "channel");
    try {
      const id = channel ? channel.id : (await session.api.openDM(to.map((x) => x.id))).channel.id;
      if (!channel) await session.loadDMMembers();
      await join(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const newHuddle = (
    <button className="primary" onClick={() => setPicking(!picking)} aria-expanded={picking}>
      {t("New huddle")}
    </button>
  );
  if (!state.huddlesOn) {
    return (
      <Page className="huddles-view" title={t("Huddles")}>
        <EmptyState icon="🎧" title={t("Huddles are not set up here")}>
          {t("Calls need a media server, which an admin adds to Lantern's settings.")}
        </EmptyState>
      </Page>
    );
  }

  const live = [...state.huddles.values()].filter((h) => state.channels.has(h.channel_id)).sort((a, b) => b.started_at.localeCompare(a.started_at));
  const channels = [...state.channels.values()]
    .map((c) => c.channel)
    .filter((c) => (c.kind === "public" || c.kind === "private") && !c.archived_at)
    .sort((a, b) => session.channelName(a.id).localeCompare(session.channelName(b.id), locale));
  // The recent calls in channels one is still in.
  const shownRecent = (recent ?? []).filter((h) => state.channels.has(h.channel_id));
  // action starts or joins a channel's call; an archived channel has none.
  const action = (channel: UUID) => {
    if (state.channels.get(channel)?.channel.archived_at) return null;
    const here = current?.channel === channel;
    const going = state.huddles.has(channel);
    return (
      <button disabled={here || busy} onClick={() => void join(channel)}>
        {here ? t("You are in it") : going ? t("Join") : t("Start huddle")}
      </button>
    );
  };

  return (
    <Page className="huddles-view" title={t("Huddles")} description={t("Talk, with video and your screen if you like, in a channel or with a few people.")} actions={newHuddle}>
      {error && <p className="error">{error}</p>}
      {picking && (
        <Card title={t("New huddle")} description={t("Call one or more people; it rings for them. Or pick a channel, for anyone in it to join.")}>
          <Form onSubmit={start}>
            <RecipientPicker to={to} onChange={setTo} autoFocus />
            <div className="form-actions">
              <button type="button" onClick={() => (setPicking(false), setTo([]))}>
                {t("Cancel")}
              </button>
              <button type="submit" className="primary" disabled={!to.length || busy}>
                {to.some((x) => x.kind === "channel") ? t("Start huddle") : t("Call")}
              </button>
            </div>
          </Form>
        </Card>
      )}
      <Card title={t("Going on now")}>
        {live.length === 0 ? (
          <p className="muted">{t("No huddles are going on in your channels and conversations.")}</p>
        ) : (
          <ul className="rows huddles-live">
            {live.map((h) => (
              <li key={h.channel_id}>
                <span className="huddle-icon live" aria-hidden>
                  <IconHeadphones size={18} />
                </span>
                <div className="row-main">
                  <span className="row-title">{session.channelName(h.channel_id)}</span>
                  <span className="sr-only">{t("With {names}", { names: h.participants.map((id) => session.name(id)).join(t(", ")) })}</span>
                  <span className="row-meta">
                    {tn(h.participants.length, "{n} person", "{n} people")} ·{" "}
                    {t("since {time}", { time: new Date(h.started_at).toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" }) })}
                  </span>
                </div>
                <span className="huddle-faces">
                  {h.participants.slice(0, 5).map((id) => (
                    <Avatar key={id} id={id} name={session.name(id)} size={24} />
                  ))}
                </span>
                {action(h.channel_id)}
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title={t("Recent")}>
        {recentError && <p className="error">{recentError}</p>}
        {recent === null ? (
          !recentError && <p className="muted">{t("Loading…")}</p>
        ) : shownRecent.length === 0 && !more ? (
          <p className="muted">{t("Huddles you were in, and calls you missed, show here once they end.")}</p>
        ) : (
          <>
            <ul className="rows huddles-recent">
              {shownRecent.map((h) => {
                const end = new Date(h.ended_at);
                const when = `${dayLabel(end)}, ${end.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })}`;
                const missed = !h.joined.includes(state.me?.id ?? "");
                return (
                  <li key={h.id}>
                    <span className={`huddle-icon ${missed ? "missed" : ""}`} aria-hidden>
                      <IconHeadphones size={18} />
                    </span>
                    <div className="row-main">
                      <span className="row-title">
                        {session.channelName(h.channel_id)} {missed && <Badge tone="bad">{t("Missed")}</Badge>}
                      </span>
                      <span className="sr-only">{t("With {names}", { names: h.joined.map((id) => session.name(id)).join(t(", ")) })}</span>
                      <span className="row-meta">
                        {missed
                          ? t("{name} called · {when}", { name: session.name(h.joined[0]), when })
                          : `${when} · ${lasted(end.getTime() - new Date(h.started_at).getTime())}`}
                      </span>
                    </div>
                    <span className="huddle-faces" title={h.joined.map((id) => session.name(id)).join(t(", "))}>
                      {h.joined.slice(0, 5).map((id) => (
                        <Avatar key={id} id={id} name={session.name(id)} size={24} />
                      ))}
                    </span>
                    {action(h.channel_id)}
                  </li>
                );
              })}
            </ul>
            {more && (
              <div className="huddles-more">
                <button onClick={() => void older()} disabled={loadingOlder}>
                  {loadingOlder ? t("Loading…") : t("Show older")}
                </button>
              </div>
            )}
          </>
        )}
      </Card>
      <Card title={t("Channels")} description={t("Meet with a whole team, or let people drop in and out.")}>
        {channels.length === 0 ? (
          <p className="muted">{t("You are in no channels yet.")}</p>
        ) : (
          <ul className="rows huddles-channels">
            {channels.map((c) => (
              <li key={c.id}>
                <span className={`huddle-icon ${state.huddles.has(c.id) ? "live" : ""}`} aria-hidden>
                  {c.kind === "private" ? "🔒" : "#"}
                </span>
                <div className="row-main">
                  <span className="row-title">{c.name}</span>
                  {state.huddles.has(c.id) && <span className="row-meta">{tn(state.huddles.get(c.id)!.participants.length, "Huddle: {n} person", "Huddle: {n} people")}</span>}
                </div>
                {action(c.id)}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Page>
  );
}
